import React, { useContext, useEffect, useState } from 'react'
import { log } from '../../services/LogService'
import { StateContext } from '../../services/StateService'
import { getRequest } from '../../services/AxiosService'
import Spinner from '../../components/Spinner'
import WhereToBuy from './WhereToBuy'
import { isWtbAvailable, resolveWtbMmmIds } from '../../../common/utils/WtbUtils'
import usePDPProductType from '../../hooks/usePDPProductType'

const WhereToBuyController = ({ getProductEndpoint }) => {
    const {
        ctaInfo,
        productDetails,
        isParent,
        replacement,
        moreOptions,
        selectedOptions,
        oneProductSelection,
        locale,
        dispatch
    } = useContext(StateContext)

    const [wtbProps, setWtbProps] = useState()
    const [widgetKey, setWidgetKey] = useState()

    const {
        isParentWithVisibleSKUs,
        isParentWithHiddenSKUs,
        isParentWithHiddenSKUsAndFilters,
        isParentWithOneHiddenSKU,
        isSKU
    } = usePDPProductType()

    useEffect(() => {
        /**
         * If user narrows to a single product option, we should do an APi call to get the details of that product
         * in order to display the inline widget for that specific product, there are some parent products (branded sites mostly)
         * that have WTB Info but they don't have any selector so if the total selected === 1
         * and there are no product options and there is WTB info we will try to load the WTB widget with
         * the parent information.
         */
        if (selectedOptions?.totalSelected === 1 && isParentWithVisibleSKUs) {
            dispatch({ type: 'pdp_set_loading_CCWTBScript', payload: true })
            const endpoint = getProductEndpoint(true)
            getRequest(endpoint)
                .then((response) => {
                    if (response?.data) {
                        if (Array.isArray(response.data.productOptions)) {
                            const singleProduct = response.data.productOptions[0]
                            const {
                                ctaInfo: singleProductCTAInfo,
                                productDetails: singleProductDetails
                            } = singleProduct
                            dispatch({
                                type: 'pdp_set_one_product_selection',
                                payload: {
                                    ...(singleProductCTAInfo || {}),
                                    ...(singleProductDetails || {})
                                }
                            })
                        } else {
                            if (ctaInfo) {
                                dispatch({
                                    type: 'pdp_set_one_product_selection',
                                    payload: {
                                        ...(ctaInfo || {}),
                                        ...(productDetails || {})
                                    }
                                })
                            }
                        }
                    }
                })
                .catch((error) => {
                    dispatch({ type: 'pdp_set_loading_CCWTBScript', payload: false })
                    log.debug(
                        'Purchase Options Error: Cannot get single child info ' + error
                    )
                })
        } else {
            if (oneProductSelection && Object.keys(oneProductSelection).length > 0) {
                dispatch({
                    type: 'pdp_set_one_product_selection',
                    payload: {}
                })
            }
        }
        /** If selectors are cleared, we reset */
        /* istanbul ignore next */
        if (
            selectedOptions?.totalSelected === moreOptions?.totalSelected &&
            wtbProps &&
            !isSKU &&
            !ctaInfo?.pdpSelectorOverride
        ) {
            setWtbProps(null)
            setWidgetKey(null)
        }
    }, [selectedOptions?.totalSelected])

    /**
     * If is a parent product with visible SKUs and user narrows down to a single selection, is not discontinued,
     * has CC (comerce conector) data and it's not a marketplace product or there is no marketplace data,
     * we will try to load the WTB widget for that product.
     */
    useEffect(() => {
        if (
            isParentWithVisibleSKUs &&
            selectedOptions?.totalSelected === 1 &&
            !replacement?.discontinued &&
            oneProductSelection?.ccSubId &&
            isWtbAvailable(oneProductSelection)
        ) {
            setWtbProps({
                ccTemplatedID: oneProductSelection?.ccTemplatedID,
                ccSubId: oneProductSelection?.ccSubId,
                mmmId: oneProductSelection?.originalMmmid,
                locale: locale,
                widgetType: 'Parent with visible SKUs (narrows to 1)'
            })
        }
    }, [oneProductSelection])

    /**
     * If is a parent product with hidden SKUs, not discontinued and has CC (comerce conector) data, we will try to load
     * the WTB widget after users filter, allowing the subsets of products/SKUs (wtbMmmIds) to show links to distributors.
     */
    useEffect(() => {
        if (
            selectedOptions?.wtbMmmIds &&
            isParentWithHiddenSKUs &&
            !replacement?.discontinued &&
            ctaInfo?.ccSubId
        ) {
            setWtbProps({
                ccTemplatedID: ctaInfo?.ccTemplatedID,
                ccSubId: ctaInfo?.ccSubId,
                mmmId: selectedOptions?.wtbMmmIds?.join(','),
                locale: locale,
                widgetType: 'Parent with hidden SKUs'
            })
        }
    }, [selectedOptions?.wtbMmmIds])

    useEffect(() => {
        if (wtbProps && !isSKU) {
            dispatch({ type: 'pdp_set_loading_CCWTBScript', payload: true })
            setWidgetKey((prevWidgetKey) => (prevWidgetKey >= 1 ? prevWidgetKey + 1 : 1))
        }
    }, [wtbProps])

    useEffect(() => {
        /** Not discontinued, has CC (comerce conector) data, and backend explicitly confirmed availability */
        const shouldSetWtbProps =
            !replacement?.discontinued && ctaInfo?.ccSubId && isWtbAvailable(ctaInfo)
        /**
         * If this is a SKU/child product and the shared WTB eligibility checks pass
         */
        if (isSKU && shouldSetWtbProps) {
            setWtbProps({
                ccTemplatedID: ctaInfo?.ccTemplatedID,
                ccSubId: ctaInfo?.ccSubId,
                mmmId: resolveWtbMmmIds({
                    isParent,
                    moreOptions,
                    ctaInfo,
                    productDetails
                })?.join(','),
                locale: locale,
                widgetType: 'SKU'
            })
            dispatch({ type: 'pdp_set_loading_CCWTBScript', payload: true })
            setWidgetKey(1)
        }

        /** If is a parent with hidden dropdowns but has children/SKUs */
        if (
            (isParentWithHiddenSKUsAndFilters ||
                (ctaInfo?.pdpSelectorOverride && moreOptions?.wtbMmmIds)) &&
            shouldSetWtbProps
        ) {
            setWtbProps({
                ccTemplatedID: ctaInfo?.ccTemplatedID,
                ccSubId: ctaInfo?.ccSubId,
                mmmId: moreOptions?.wtbMmmIds.join(','),
                locale: locale,
                widgetType: 'Parent with hidden SKUs and filters'
            })
        } else if (isParentWithOneHiddenSKU && shouldSetWtbProps) {
            setWtbProps({
                ccTemplatedID: ctaInfo?.ccTemplatedID,
                ccSubId: ctaInfo?.ccSubId,
                mmmId: resolveWtbMmmIds({
                    isParent,
                    moreOptions,
                    ctaInfo,
                    productDetails
                })?.join(','),
                locale: locale,
                widgetType: 'Parent with one hidden SKU'
            })
        }
    }, [])

    return (
        <>
            {wtbProps && widgetKey && (
                <>
                    {ctaInfo?.loadingCCWTBScript && (
                        <div className="sps2-pdp_purchase--CCloader">
                            <Spinner />
                        </div>
                    )}
                    <WhereToBuy
                        key={widgetKey}
                        ccTemplatedID={wtbProps?.ccTemplatedID}
                        ccSubId={wtbProps?.ccSubId}
                        mmmId={wtbProps?.mmmId}
                        locale={locale}
                        widgetType={wtbProps?.widgetType}
                    />
                </>
            )}
        </>
    )
}

export default WhereToBuyController
